/* eslint-disable @typescript-eslint/ban-ts-comment */
import OptionItemLayout from "@/app/app-shared/homepage-layout/forms/OptionItemLayout.tsx";
import { useModal } from "@/app/shared/modal-views/use-modal";
import ChartPie from "@/components/chart-pie";
import ScoreDetails from "@/components/score-details";
import Timeline from "@/components/timeline";
import {
  COUNTRY_SG_CODE,
  ENQUIRY_ENTRY_CONTACT_STATUS,
} from "@/config/constants";
import useProfile from "@/hooks/use-profile";
import useApi from "@/hooks/useApi";
import { leadScore } from "@/utilities/functions/lead";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import MessageRepository from "@/utilities/repositories/Message.ts";
import SettingsRepository from "@/utilities/repositories/Settings";
import { IDistributedRoundRobin } from "@/utilities/types/DistributedRoundRobin";
import {
  EActionTrackingType,
  EEnquiryEntryContactStatus,
  ESubmitFrom,
  IEnquiryEntry,
} from "@/utilities/types/EnquiryEntry";
import { TAgencySetting } from "@/utilities/types/Setting";
import { ITrackingTimeline } from "@/utilities/types/TrackingTimeline.ts";
import { Roles } from "@/utilities/types/Users";
import { IResponseData } from "@/utilities/types/requests";
import cn from "@/utils/class-names";
import isValidJson from "@/utils/is-valid-json.ts";
import { createWhatsappMessage } from "@/utils/string";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiChevronDown, BiDevices, BiFlag } from "react-icons/bi";
import { CgRename } from "react-icons/cg";
import { FaRegGrinStars } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { GoPin } from "react-icons/go";
import { IoLocationOutline } from "react-icons/io5";
import { LuShoppingCart } from "react-icons/lu";
import {
  PiAddressBookLight,
  PiBrowser,
  PiBuildingLight,
  PiCalendarLight,
  PiConfettiLight,
  PiFingerprintLight,
  PiHouseLineDuotone,
  PiMailboxLight,
  PiMapPinAreaDuotone,
  PiPhoneLight,
  PiPowerLight,
  PiUserCircleLight,
  PiWarningLight,
  PiXBold,
} from "react-icons/pi";
import {
  Accordion,
  ActionIcon,
  Button,
  Select,
  SelectOption,
  Textarea,
  Title,
} from "rizzui";
import LogsActionTable, { LogActionTableRef } from "../table/logs-action-table";
import { openContactWhatsApp } from "@/utilities/functions/common";
import QuillEditor from "@/components/ui/quille-editor/quill-editor.tsx";
import { LeadQualityScore } from "@/app/app-shared/enquiry-entrties/lead-quality-score";
import { GrResources } from "react-icons/gr";

import { RiAdvertisementLine } from "react-icons/ri";

interface TSelectMessage {
  label: string;
  value: string;
  content: string;
  isAdmin: boolean;
}

interface TSelectOptionMessage {
  label: string;
  value: string;
}

export default function EnquiryEntryDetail({
  data,
  isModalView,
  onRefresh,
  isAdmin,
}: {
  data?: IEnquiryEntry | IDistributedRoundRobin;
  isModalView?: boolean;
  onRefresh?: () => void;
  isAdmin?: boolean;
}) {
  const { closeModal } = useModal();
  const { request: getAgencySetting, response: agencySettingData } = useApi<
    IResponseData<TAgencySetting>
  >({
    request: SettingsRepository.getAgencySetting,
  });

  const [contactStatus, setContactStatus] =
    useState<EEnquiryEntryContactStatus>(
      (data as IEnquiryEntry)?.contactStatus || EEnquiryEntryContactStatus.NEW,
    );

  const [messageSelected, setMessageSelected] = useState<TSelectMessage>();
  const [valueMessage, setValueMesagge] = useState<string>();
  const { profile } = useProfile();
  const [optionsMessage, setOptionsMessage] = useState<TSelectOptionMessage[]>(
    [],
  );

  const tableLogActionRef = useRef<LogActionTableRef>(null);

  const [whatsappMessage, setWhatsappMessage] = useState<string>("");
  const [userNote, setUserNote] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState<string | null>(null);

  const { parsedInterestedIn, parsedUnitTypes } = useMemo(() => {
    try {
      const parsedInterestedIn: string[] = JSON.parse(data?.interestedIn || "");
      const parsedUnitTypes: string[] = JSON.parse(data?.unitTypes || "");
      return { parsedInterestedIn, parsedUnitTypes };
    } catch (e) {
      return { parsedInterestedIn: [], parsedUnitTypes: [] };
    }
  }, [data?.interestedIn, data?.unitTypes]);

  const { request: getAllMessageByAgency, response: messageByAgencyResponse } =
    useApi({
      request: MessageRepository.getMessagesByAgency,
    });

  const { request: getAllMessagesByAdmin, response: messageByAdminResponse } =
    useApi({
      request: MessageRepository.getMessagesByAdmin,
    });

  const {
    request: updateEnquiryEntry,
    response: updateEnquiryEntryResponse,
    loading,
  } = useApi({
    request: EnquiryEntriesRepository.update,
    enableToast: true,
  });

  const { request: getLogsByEnquiryId, response: enquiryLogsResponse } = useApi(
    {
      request: EnquiryEntriesRepository.getLogsById,
    },
  );

  const isEnquiryEntry = (obj?: any): obj is IEnquiryEntry => {
    return "note" in obj;
  };

  const enquiryEntryData = isEnquiryEntry(data)
    ? (data as IEnquiryEntry)
    : undefined;

  useEffect(() => {
    getAgencySetting();
    if (profile?.role === Roles.ADMIN) {
      if (typeof getAllMessagesByAdmin === "function") {
        getAllMessagesByAdmin({});
      }
    } else {
      if (typeof getAllMessageByAgency === "function") {
        getAllMessageByAgency({});
      }
    }

    if (data?.id && typeof getLogsByEnquiryId === "function") {
      getLogsByEnquiryId(data.id);
    }
  }, [
    data?.id,
    getAgencySetting,
    getAllMessageByAgency,
    getAllMessagesByAdmin,
    getLogsByEnquiryId,
    profile?.role,
  ]);

  useEffect(() => {
    const temp =
      profile?.role === Roles.ADMIN
        ? messageByAdminResponse?.data?.items
        : messageByAgencyResponse?.data?.items;
    const Options = (Array.isArray(temp) ? temp : [])?.map((item) => ({
      label: item.subject,
      value: item.id,
      content: item.content,
      isAdmin: item.isAdmin,
    }));
    setMessageSelected(Options?.[0]);
    setValueMesagge(Options?.[0]?.value || "");
    setOptionsMessage(Options);
  }, [
    messageByAdminResponse?.data?.items,
    messageByAgencyResponse?.data?.items,
    profile?.role,
  ]);

  useEffect(() => {
    if (optionsMessage) {
      const findOption: TSelectOptionMessage | undefined = optionsMessage.find(
        (item) => item.value === valueMessage,
      );
      // @ts-ignore
      setMessageSelected(findOption);
    }
  }, [optionsMessage, valueMessage]);

  useEffect(() => {
    const parsedMessage = createWhatsappMessage(
      messageSelected?.content || "",
      {
        "Recipient's Name": data?.name || "",
        customer_name: data?.name || "",
        customer_email: data?.email || "",
        customer_phone: data?.phone || "",
        agent_name: `${data?.user?.firstName ? `${data?.user?.firstName} ` : ""}${data?.user?.lastName || ""}`,
        agent_email: data?.user?.email || "",
        project_name:
          typeof data?.project?.name === "object"
            ? data?.project?.name?.en
            : data?.project?.name || "",
        project_address:
          typeof data?.project?.address === "object"
            ? data?.project?.address?.en
            : data?.project?.address || "",
        project_area: data?.project?.area || "",
        project_tenure: data?.project?.tenure || "",
        project_tenure_effect_from:
          data?.project?.tenureEffectFrom &&
          dayjs(data?.project?.tenureEffectFrom).isValid()
            ? dayjs(data?.project?.tenureEffectFrom).format("DD MMM YYYY")
            : "",
        appointment_date_time: dayjs(data?.appointment).isValid()
          ? dayjs(data?.appointment).format("MMMM DD, YYYY hh:mm A")
          : "Not Given",
      },
    );

    setWhatsappMessage(parsedMessage);
  }, [messageSelected?.content]);

  useEffect(() => {
    if (!data) return;
    if (!data?.id) return;

    if (isEnquiryEntry(data)) {
      data.note && setUserNote(data.note);
      data.adminNote && setAdminNote(data.adminNote);
    }
  }, [data?.id, isEnquiryEntry(data)]);

  const timelineData =
    typeof data?.tracking === "string" && isValidJson(data.tracking)
      ? JSON.parse(data.tracking)
      : ({} as ITrackingTimeline);

  let convertPhone = data?.phone;
  const subString = " ";
  if (convertPhone) {
    !convertPhone.includes(COUNTRY_SG_CODE) &&
      (convertPhone = COUNTRY_SG_CODE + convertPhone);
    convertPhone =
      convertPhone.substring(0, 3) + subString + convertPhone.substring(3);
    convertPhone =
      convertPhone.substring(0, 8) + subString + convertPhone.substring(8);
  } else {
    convertPhone = "N/A";
  }

  const handleOpenWhatsapp = async () => {
    try {
      openContactWhatsApp(
        data?.phone?.startsWith("0")
          ? `+65${data?.phone?.slice(1) || ""}`
          : data?.phone || "",
        whatsappMessage,
      );
      await EnquiryEntriesRepository.createLogAction({
        submissionId: data?.id || "",
        trackingType: EActionTrackingType.CLICK_CONTACT_WHATSAPP_BUTTON,
      });
      await tableLogActionRef.current?.refresh();
    } catch (error) {
      console.log(error);
    }
  };

  const formatDisplayValue = (
    value: string | string[] | undefined | null,
  ): string => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "N/A";
    }
    if (typeof value === "string") {
      return value.length > 0 ? value : "N/A";
    }
    return "N/A";
  };

  // Helper to display lead source
  const getLeadSourceDisplay = useCallback(() => {
    if (
      [ESubmitFrom.LANDING_PAGE, ESubmitFrom.LISTING_PAGE].includes(
        data?.submitFrom as ESubmitFrom,
      )
    ) {
      return "Lead Form Submission";
    }
    if (data?.leadSource) {
      return data.leadSource;
    }
    return "N/A";
  }, [data?.submitFrom, data?.leadSource]);

  return (
    <div className="block">
      <div className="flex items-center justify-between border-b border-gray-200 p-5 md:p-7">
        <Title
          as="h3"
          className="font-lexend text-xl font-semibold md:text-2xl"
        >
          Lead Details
        </Title>
        {isModalView && (
          <ActionIcon
            size="sm"
            variant="text"
            onClick={() => closeModal()}
            className="p-0 text-gray-500 hover:!text-gray-900"
          >
            <PiXBold className="h-5 w-5" />
          </ActionIcon>
        )}
      </div>
      <div className="p-5 md:px-7 md:pb-7 items-center justify-center md:pt-6 grid grid-cols-1 lg:grid-cols-3 gap-y-4 gap-x-6">
        <div className="border border-gray-200 rounded-lg p-4 col-span-full">
          <div
            className={
              "flex flex-col justify-center items-center sm:flex flex-wrap"
            }
          >
            <div className={cn(timelineData?.timeline?.length > 2 && "w-full")}>
              <Title as="h3" className="mb-5 font-lexend  font-medium text-lg">
                How was this lead acquired?
              </Title>
              <Timeline timelineData={timelineData} />
            </div>
            <div className="relative text-center flex flex-col gap-2 h-fit col-span-1 w-fit max-lg:w-full">
              <ChartPie totalInitial={data?.score || 0} />
              <p className="font-bold">Lead Quality Score</p>
              <LeadQualityScore
                title="Lead Quality Score Detail"
                button="View Detail"
              >
                <div
                  className="rounded-lg border min-h-[254px]"
                  color={
                    leadScore(enquiryLogsResponse?.data?.score || 0)
                      ?.rizzuiColor
                  }
                  style={{
                    borderColor: leadScore(
                      enquiryLogsResponse?.data?.score || 0,
                    )?.color,
                  }}
                >
                  <ScoreDetails enquiryLogsResponse={enquiryLogsResponse} />
                </div>
              </LeadQualityScore>
            </div>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 col-span-full">
          <Title as="h3" className="mb-5 font-lexend  font-medium text-lg">
            Summary
          </Title>
          <ul className="space-y-4 text-xs sm:text-sm">
            <li className="flex flex-start md:items-center">
              <PiCalendarLight className="me-2  w-5 shrink-0 text-xl" />
              <div className="flex flex-wrap md:flex-nowrap items-start gap-1 md:items-center">
                Preferred Appointment Date & Time:
                <span className="font-medium text-gray-1000">
                  {dayjs(data?.appointment).isValid()
                    ? dayjs(data?.appointment).format("MMMM DD, YYYY hh:mm A")
                    : "Not Given"}
                </span>
              </div>
            </li>
            <li className="flex flex-start">
              <PiBuildingLight className="me-2 w-5 shrink-0 text-xl " />
              <div className="flex flex-wrap items-start gap-1 md:items-center">
                <span className="whitespace-nowrap">Unit types selected:</span>
                <span className="font-medium text-gray-1000">
                  {formatDisplayValue(parsedUnitTypes)}
                </span>
              </div>
            </li>
            <li className="flex flex-start md:items-center">
              <FaRegGrinStars className="me-2 w-5 shrink-0 text-xl text-gray-400" />
              <div className="flex flex-wrap md:flex-nowrap items-start gap-1 md:items-center">
                <span className="whitespace-nowrap"> Interested in:</span>

                <span className="font-medium text-gray-1000">
                  {(Array?.isArray(parsedInterestedIn) &&
                    parsedInterestedIn?.join(", ")) ||
                    "N/A"}
                </span>
              </div>
            </li>
            <li className="flex items-center">
              <div className="flex items-baseline">
                <BiFlag className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />

                <span className="whitespace-nowrap">Country (Region):</span>
              </div>

              <span className="ps-2 font-medium text-gray-1000">Singapore</span>
            </li>{" "}
            <li className="flex items-center">
              <div className="flex items-baseline">
                <IoLocationOutline className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                <span className="whitespace-nowrap">IP Address:</span>
              </div>

              <span className="ps-2 font-medium text-gray-1000">
                {data?.ip || "N/A"}
              </span>
            </li>
            <li className="flex items-center">
              <div className="flex items-baseline">
                <PiBrowser className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />

                <span className="whitespace-nowrap">Browser Type:</span>
              </div>

              <span className="ps-2 font-medium text-gray-1000">
                {data?.browser || "N/A"}
              </span>
            </li>
            <li className="flex items-center">
              <div className="flex items-baseline">
                <BiDevices className="me-2  self-center w-5 shrink-0 text-xl text-gray-400" />
                <span className="whitespace-nowrap">Device Type:</span>
              </div>
              <span className="ps-2 font-medium text-gray-1000">
                {data?.device?.model && data?.device?.vendor
                  ? `${data?.device?.model} | ${data?.device?.vendor}`
                  : "N/A"}
              </span>
            </li>
            <li className="flex items-center">
              <div className="flex items-center">
                <GrResources className="me-2 w-5 shrink-0 text-xl text-gray-400" />
                <span className="whitespace-nowrap">Source:</span>
              </div>
              <span className="ps-2 font-medium text-gray-1000">
                {getLeadSourceDisplay()}
              </span>
            </li>
            {/* <li className="flex items-center">
              <div className="flex items-center">
                <GrResources className="me-2 w-5 shrink-0 text-xl text-gray-400" />
                <span className="whitespace-nowrap">Lead Feedback:</span>
              </div>
              <span className="ps-2 font-medium text-gray-1000"></span>
            </li> */}
            {enquiryEntryData && (
              <li className="flex items-center">
                <div className="flex items-center">
                  <RiAdvertisementLine className="me-2 w-5 shrink-0 text-xl text-gray-400" />
                  <span className="whitespace-nowrap">GCLID:</span>
                </div>
                <span className="ps-2 font-medium text-gray-1000">
                  {enquiryEntryData?.gclid}
                </span>
              </li>
            )}
          </ul>
        </div>

        <div className="border border-gray-200 rounded-lg p-4 col-span-full">
          <div className={"grid grid-cols-2 gap-8"}>
            <div className={"col-span-2 lg:col-span-1"}>
              <Title
                as="h3"
                className="mb-5 font-lexend flex items-center font-medium text-lg"
              >
                Project
              </Title>
              <ul className="space-y-4 text-xs sm:text-sm">
                <li className="flex items-center">
                  <CgRename className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                  Name:
                  <span className="ps-2 font-medium text-gray-1000">
                    {(typeof data?.project?.name === "object"
                      ? data?.project?.name?.en
                      : data?.project?.name) || "N/A"}
                  </span>
                </li>

                <li className="flex items-center">
                  <PiMapPinAreaDuotone className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                  Address:
                  <span className="ps-2 font-medium text-gray-1000">
                    {data?.project?.address?.en || "N/A"}
                  </span>
                </li>
                <li className="flex items-center">
                  <PiHouseLineDuotone className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                  Area:
                  <span className="ps-2 font-medium text-gray-1000">
                    {data?.project?.area || "N/A"}
                  </span>
                </li>
                <li className="flex items-center">
                  <LuShoppingCart className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                  Tenure:
                  <span className="ps-2 font-medium text-gray-1000">
                    {data?.project?.tenure || "N/A"}
                  </span>
                </li>
                <li className="flex items-center">
                  <GoPin className="me-2  self-center  w-5 shrink-0 text-xl text-gray-400" />
                  Tenure effect from:
                  <span className="ps-2 font-medium text-gray-1000">
                    {data?.project?.tenureEffectFrom &&
                    dayjs(data?.project?.tenureEffectFrom).isValid()
                      ? dayjs(data?.project?.tenureEffectFrom).format(
                          "DD MMM YYYY",
                        )
                      : "N/A"}
                  </span>
                </li>
              </ul>
            </div>
            <div className={"col-span-2 lg:col-span-1"}>
              <div>
                <Button
                  className={"mb-3 w-full h-fit"}
                  onClick={async () => {
                    // if (isIos) return;
                    await handleOpenWhatsapp();
                  }}
                  // onTouchStart={async () => {
                  //   // if (!isIos) return;
                  //   await handleOpenWhatsapp();
                  // }}
                >
                  <span className={"flex gap-2 items-center"}>
                    <FaWhatsapp /> Contact {convertPhone} on Whatsapp
                  </span>
                </Button>
              </div>
              {optionsMessage.length > 0 && (
                <Select
                  options={optionsMessage}
                  value={valueMessage}
                  onChange={setValueMesagge}
                  label="Layout"
                  className="mb-3"
                  getOptionValue={(option: SelectOption) => option.value}
                  displayValue={(selected: string) => {
                    const findOption: TSelectOptionMessage | undefined =
                      optionsMessage?.find(
                        (option) => option.value === selected,
                      );
                    // @ts-ignore
                    setMessageSelected(findOption);
                    // @ts-ignore
                    return findOption.label;
                  }}
                  getOptionDisplayValue={(option) => {
                    return <OptionItemLayout option={option} />;
                  }}
                  dropdownClassName={"z-modal [&_li>div]:w-full"}
                />
              )}

              <div className={"col-span-full"}>
                <Textarea
                  label="Content"
                  placeholder="Content Message"
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                />
              </div>

              <div className="col-span-full mt-2">
                <Select
                  options={ENQUIRY_ENTRY_CONTACT_STATUS}
                  onChange={async (value: EEnquiryEntryContactStatus) => {
                    updateEnquiryEntry(data?.id || "", {
                      contactStatus: value,
                    }).then(async () => {
                      setContactStatus(value);
                      await tableLogActionRef.current?.refresh();
                    });
                  }}
                  value={contactStatus}
                  label="Contact Status"
                  className="mb-3"
                  getOptionValue={(option: SelectOption) => option.value}
                  displayValue={(selected: string) => {
                    const findOption: TSelectOptionMessage | undefined =
                      ENQUIRY_ENTRY_CONTACT_STATUS?.find(
                        (option) => option.value === selected,
                      );

                    return findOption?.label;
                  }}
                  getOptionDisplayValue={(option) => {
                    return <OptionItemLayout option={option} />;
                  }}
                  dropdownClassName={"z-modal [&_li>div]:w-full"}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "border border-gray-200 rounded-lg p-4 h-full",
            "col-span-full lg:col-span-1",
          )}
        >
          <Title as="h3" className="mb-5 font-lexend  font-medium text-lg">
            Customer
          </Title>

          <ul className="space-y-4 text-xs sm:text-sm">
            <li className="flex items-center flex-wrap h-full gap-5">
              <div className="flex items-center">
                <PiUserCircleLight className="me-2  w-5 shrink-0 text-xl" />
                Name:
                <span className="ps-2 font-medium text-gray-1000">
                  {data?.name || "N/A"}
                </span>
              </div>
              <div className="flex items-center">
                <PiConfettiLight className="me-2  w-5 shrink-0 text-xl" />
                Score:
                <span
                  className="ps-2 flex items-center justify-center font-medium text-gray-1000"
                  style={{ color: leadScore(data?.score || 0)?.color }}
                >
                  {data?.score || 0}
                  {data?.score === 0 && <PiWarningLight className="h-4 w-4" />}
                </span>
              </div>
            </li>
            <li className="flex items-center">
              <PiPhoneLight className="me-2  w-5 shrink-0 text-xl" />
              Phone:
              <span className="ps-2 font-medium text-gray-1000">
                {convertPhone}
              </span>
            </li>
            <li className="flex items-start">
              <PiMailboxLight className="me-2  w-5 shrink-0 text-xl" />
              <div className="flex flex-wrap items-start gap-1 md:items-center">
                Email:
                <span className="font-medium text-gray-1000">
                  {data?.email || "N/A"}
                </span>
              </div>
            </li>
          </ul>
        </div>

        {profile?.role === Roles.ADMIN && (
          <div className="border border-gray-200 rounded-lg p-4 h-full col-span-full lg:col-span-1">
            <Title as="h3" className="mb-5 font-lexend  font-medium text-lg">
              Agent
            </Title>

            <ul className="space-y-4 text-xs sm:text-sm">
              <li className="flex items-center">
                <PiUserCircleLight className="me-2  w-5 shrink-0 text-xl" />
                Name:
                <span className="ps-2 font-medium text-gray-1000">
                  {data?.user?.firstName + " " + data?.user?.lastName || "N/A"}
                </span>
              </li>

              <li className="flex items-center">
                <PiMailboxLight className="me-2  w-5 shrink-0 text-xl" />
                Email:
                <span className="ps-2 font-medium text-gray-1000 break-words whitespace-pre-wrap">
                  {data?.user?.email || "N/A"}
                </span>
              </li>
            </ul>
          </div>
        )}

        <div
          className={cn(
            "border border-gray-200 h-full rounded-lg p-4",
            "col-span-full lg:odd:col-span-2 lg:even:col-span-1",
          )}
        >
          <Title as="h3" className="mb-5 font-lexend  font-medium text-lg">
            CEA
          </Title>

          <ul className="space-y-4 text-xs sm:text-sm">
            <li className="flex items-center">
              <PiAddressBookLight className="me-2  w-5 shrink-0 text-xl" />
              CEA Name:
              <span className="ps-2 font-medium text-gray-1000">
                {data?.ceaInfo?.name || "N/A"}
              </span>
            </li>
            <li className="flex items-center">
              <PiPowerLight className="me-2  w-5 shrink-0 text-xl" />
              CEA Reg No:
              <span className="ps-2 font-medium text-gray-1000">
                {data?.ceaInfo?.registrationNumber || "N/A"}
              </span>
            </li>
            <li className="flex md:items-center items-start">
              <PiFingerprintLight className="me-2 self-start w-5 shrink-0 text-xl" />
              <div className="flex flex-wrap items-start gap-1 md:items-center">
                <span className="whitespace-nowrap">CEA Agency:</span>
                <span className="font-medium whitespace-nowrap text-gray-1000">
                  {data?.ceaInfo?.currentEa || "N/A"}
                </span>
              </div>
            </li>
          </ul>
        </div>

        {isEnquiryEntry(data) && (
          <div className={"col-span-full flex flex-col gap-6"}>
            <QuillEditor
              value={userNote}
              onChange={(value) => setUserNote(value)}
              label="User Notes"
              className="[&_.ql-editor]:min-h-[120px] [&_.ql-editor]:max-h-[180px] text-lg"
              labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
              description="Key in your notes here."
              descriptionClassName="text-gray-500 text-sm mb-1.5"
              error={data.note === userNote ? undefined : ""}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link", "clean"],
                ],
              }}
            />
            <QuillEditor
              value={adminNote}
              onChange={(value) => setAdminNote(value)}
              label="ProjectSG Notes"
              className="[&_.ql-editor]:min-h-[120px] [&_.ql-editor]:max-h-[180px] text-lg"
              labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
              description="Editor access is restricted to ProjectSG"
              descriptionClassName="text-gray-500 text-sm mb-1.5"
              error={data.note === adminNote ? undefined : ""}
              readOnly={!isAdmin}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link", "clean"],
                ],
              }}
            />
            <div className="flex justify-end">
              <Button
                disabled={
                  data.note === userNote && data.adminNote === adminNote
                }
                isLoading={loading}
                onClick={async () => {
                  if (!data?.id) return;
                  await updateEnquiryEntry(data.id, {
                    note: userNote,
                    adminNote: adminNote,
                  });

                  onRefresh && onRefresh();
                }}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        <div
          className={cn(
            "border border-gray-200 h-full rounded-lg p-4 col-span-full",
          )}
        >
          <Accordion>
            <Accordion.Header>
              {({ open }) => (
                <div className="flex w-full cursor-pointer items-center justify-between py-5 text-xl font-semibold">
                  Activity history
                  <BiChevronDown
                    className={cn(
                      "h-5 w-5 -rotate-90 transform transition-transform duration-300",
                      open && "-rotate-0",
                    )}
                  />
                </div>
              )}
            </Accordion.Header>
            <Accordion.Body className="mb-7">
              <LogsActionTable
                tableDataPushParams={false}
                submissionId={data?.id}
                ref={tableLogActionRef}
              />
            </Accordion.Body>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
